// src/Views/ProfitGuide/ProfitGuide.js
// 🎯 How to Make Consistent Profits Guide
// Updated: February 14, 2026 - Ignis + Risk Management
// Works as: Popup (after login) + Standalone page (/guide)

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import './ProfitGuide.css';

const STORAGE_KEY = 'aipredict_profit_guide_dismissed';

const ProfitGuide = ({ isPopup = false, onClose }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [currentStep, setCurrentStep] = useState(0);
    const [isVisible, setIsVisible] = useState(false);

    // Check if should show popup
    useEffect(() => {
        if (isPopup) {
            const dismissed = localStorage.getItem(STORAGE_KEY);
            if (!dismissed && user) {
                // Small delay for smoother UX after login
                const timer = setTimeout(() => setIsVisible(true), 1000);
                return () => clearTimeout(timer);
            }
        } else {
            // Standalone page - always visible
            setIsVisible(true);
        }
    }, [isPopup, user]);

    const handleDismiss = () => {
        localStorage.setItem(STORAGE_KEY, 'true');
        setIsVisible(false);
        if (onClose) onClose();
    };

    const handleNext = () => {
        if (currentStep < steps.length - 1) {
            setCurrentStep(prev => prev + 1);
        }
    };

    const handlePrev = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleGoToStep = (index) => {
        setCurrentStep(index);
    };

    // Guide Steps Content
    const steps = [
        {
            id: 'welcome',
            icon: 'solar:cup-star-bold',
            title: 'How to Make Consistent Profits',
            subtitle: 'Your complete guide to winning with AiPredict',
            content: (
                <div className="guide__welcome">
                    <div className="guide__welcome-badge">
                        <Icon icon="solar:verified-check-bold" />
                        <span>Ignis</span>
                    </div>
                    
                    <p className="guide__intro">
                        AiPredict's <strong>Ignis</strong> connects to your Bybit account and trades 
                        crypto automatically — trend detection, AI-reviewed entries, and multi-tier take profits. 
                        This guide will show you how Ignis works and how to maximize your results.
                    </p>

                    <div className="guide__single-visual">
                        <div className="guide__single-item">
                            <Icon icon="solar:wallet-money-bold" className="guide__single-icon" />
                            <span>Crypto Auto-Trading</span>
                            <span className="guide__single-desc">Bybit • Automated • 24/7</span>
                        </div>
                    </div>

                    <div className="guide__example-box">
                        <div className="guide__example-header">
                            <Icon icon="solar:wallet-money-bold" />
                            <span>Ignis Subscription Tiers</span>
                        </div>
                        <div className="guide__example-content">
                            <div className="guide__capital-single">
                                <div className="guide__capital-total">
                                    <span className="guide__capital-label">🥉 Bronze</span>
                                    <span className="guide__capital-amount">$500/mo</span>
                                    <span className="guide__capital-label">Up to $2K capital</span>
                                </div>
                                <div className="guide__capital-total">
                                    <span className="guide__capital-label">🥈 Silver</span>
                                    <span className="guide__capital-amount">$1,000/mo</span>
                                    <span className="guide__capital-label">Up to $5K capital</span>
                                </div>
                                <div className="guide__capital-total">
                                    <span className="guide__capital-label">🥇 Gold</span>
                                    <span className="guide__capital-amount">$2,000/mo</span>
                                    <span className="guide__capital-label">Up to $10K capital</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <p className="guide__note">
                        <Icon icon="solar:info-circle-bold" />
                        <strong>Golden Rule:</strong> Only trade with money you can afford to lose completely. 
                        Never use rent money, savings, or borrowed funds.
                    </p>
                </div>
            )
        },
        {
            id: 'risk',
            icon: 'solar:shield-check-bold',
            title: 'Risk Management First',
            subtitle: 'The foundation of profitable trading',
            content: (
                <div className="guide__risk">
                    <div className="guide__danger-box">
                        <Icon icon="solar:danger-triangle-bold" />
                        <div>
                            <strong>95% of traders lose money.</strong> The difference between winners and losers isn't 
                            the strategy—it's risk management. Even with automated trading, understanding these principles is essential.
                        </div>
                    </div>

                    <h4 className="guide__section-title">How Ignis Manages Risk</h4>

                    <div className="guide__pillars">
                        <div className="guide__pillar">
                            <div className="guide__pillar-icon">
                                <Icon icon="solar:chart-square-bold" />
                            </div>
                            <h5>1. Automatic Position Sizing</h5>
                            <p>Ignis calculates position sizes based on your <strong>tier's capital cap</strong>, never over-allocating.</p>
                            <div className="guide__pillar-example">
                                <span>Bronze: up to $2K · Silver: up to $5K · Gold: up to $10K</span>
                            </div>
                        </div>

                        <div className="guide__pillar">
                            <div className="guide__pillar-icon">
                                <Icon icon="solar:stop-bold" />
                            </div>
                            <h5>2. Auto Stop Losses</h5>
                            <p>Every trade gets a stop loss <strong>on entry</strong>. After TP1 hits, the stop moves to breakeven — locking in risk-free trades.</p>
                            <div className="guide__pillar-example">
                                <span>SL set on entry → Breakeven after TP1</span>
                            </div>
                        </div>

                        <div className="guide__pillar">
                            <div className="guide__pillar-icon">
                                <Icon icon="solar:hand-money-bold" />
                            </div>
                            <h5>3. Multi-Tier Take Profits</h5>
                            <p>Ignis secures gains progressively: <strong>TP1 → TP2 → TP3</strong>. No greed, no guesswork — profits locked at each level.</p>
                            <div className="guide__pillar-example">
                                <span>30% at TP1 · 40% at TP2 · 30% at TP3</span>
                            </div>
                        </div>
                    </div>

                    <div className="guide__risk-table">
                        <div className="guide__risk-table-header">
                            <Icon icon="solar:calculator-bold" />
                            <span>Tier Capital Allocation</span>
                        </div>
                        <div className="guide__risk-table-content">
                            <div className="guide__risk-row guide__risk-row--header">
                                <span>Tier</span>
                                <span>Price/mo</span>
                                <span>Max Capital</span>
                            </div>
                            <div className="guide__risk-row">
                                <span>🥉 Bronze</span>
                                <span>$500</span>
                                <span>$2,000</span>
                            </div>
                            <div className="guide__risk-row">
                                <span>🥈 Silver</span>
                                <span>$1,000</span>
                                <span>$5,000</span>
                            </div>
                            <div className="guide__risk-row">
                                <span>🥇 Gold</span>
                                <span>$2,000</span>
                                <span>$10,000</span>
                            </div>
                        </div>
                    </div>

                    <div className="guide__key-point">
                        <Icon icon="solar:lightbulb-bolt-bold" />
                        <p>
                            <strong>Why This Matters:</strong> Ignis's built-in risk controls — stop losses, breakeven protection, 
                            daily circuit breakers — are designed to preserve capital through losing streaks so you stay in the game long-term.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'crypto',
            icon: 'solar:chart-2-bold',
            title: 'How Ignis Works',
            subtitle: 'Automated entries, exits, and risk management',
            content: (
                <div className="guide__crypto">
                    <div className="guide__prereq">
                        <Icon icon="solar:check-circle-bold" className="guide__prereq-icon" />
                        <div>
                            <strong>Prerequisites:</strong>
                            <ul>
                                <li>Bybit account with USDT balance</li>
                                <li>API keys created (Trade Only — No Withdrawals)</li>
                                <li>API keys submitted on the Crypto AI page</li>
                                <li>USDT subscription payment sent</li>
                            </ul>
                        </div>
                    </div>

                    <h4 className="guide__section-title">The Strategy: Range Detection + AI Review</h4>
                    <p>
                        Ignis monitors multiple assets and timeframes 24/7. When it detects a consolidation range forming, 
                        it waits for a high-probability setup (fakeout + reclaim or rejection) before entering.
                    </p>

                    <div className="guide__trade-example">
                        <div className="guide__trade-header">
                            <Icon icon="solar:chart-2-bold" />
                            <span>Ignis Trade Lifecycle</span>
                        </div>
                        <div className="guide__trade-content">
                            <div className="guide__trade-pair">
                                <span className="guide__trade-symbol">BTC</span>
                                <span className="guide__trade-action">LONG (Reclaim)</span>
                            </div>
                            
                            <div className="guide__trade-levels">
                                <div className="guide__trade-level guide__trade-level--entry">
                                    <span className="guide__level-label">🟢 Entry</span>
                                    <span className="guide__level-value">Ignis enters automatically</span>
                                    <span className="guide__level-amount">After AI confirms setup</span>
                                </div>
                                <div className="guide__trade-level guide__trade-level--tp">
                                    <span className="guide__level-label">🎯 TP1 (30%)</span>
                                    <span className="guide__level-value">Close 30% at POC</span>
                                    <span className="guide__level-gain">Move SL to breakeven</span>
                                </div>
                                <div className="guide__trade-level guide__trade-level--tp">
                                    <span className="guide__level-label">🎯 TP2 (70%)</span>
                                    <span className="guide__level-value">Close to 70% at VAH/VAL</span>
                                    <span className="guide__level-gain">Trail remaining 30%</span>
                                </div>
                                <div className="guide__trade-level guide__trade-level--tp">
                                    <span className="guide__level-label">🎯 TP3 (100%)</span>
                                    <span className="guide__level-value">Close final 30%</span>
                                    <span className="guide__level-gain">Trade complete</span>
                                </div>
                                <div className="guide__trade-level guide__trade-level--sl">
                                    <span className="guide__level-label">🛑 Stop Loss</span>
                                    <span className="guide__level-value">Auto-set on entry</span>
                                    <span className="guide__level-note">Moves to breakeven after TP1</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="guide__steps-list">
                        <h4 className="guide__section-title">What You Need To Do</h4>
                        <ol>
                            <li><strong>Keep USDT funded</strong> → Ensure your Bybit account has sufficient USDT balance</li>
                            <li><strong>Join Telegram</strong> → Get real-time alerts for every trade entry and exit</li>
                            <li><strong>Check your dashboard</strong> → Monitor active trades, win rate, and PnL</li>
                            <li><strong>Don't interfere</strong> → Do NOT manually trade the same assets Ignis manages</li>
                            <li><strong>Be patient</strong> → Ignis waits for high-probability setups only</li>
                        </ol>
                    </div>

                    <div className="guide__warning">
                        <Icon icon="solar:shield-check-bold" />
                        <div>
                            <strong>Fully Autonomous:</strong> Ignis handles entries, stop losses, take profits, and position sizing. 
                            You don't need to watch charts or execute anything manually. Just let it run.
                        </div>
                    </div>
                </div>
            )
        },
        {
            id: 'safety',
            icon: 'solar:shield-check-bold',
            title: 'Ignis Safety & Security',
            subtitle: 'Your funds, your wallet, always',
            content: (
                <div className="guide__polymarket">
                    <h4 className="guide__section-title">How Your Account is Protected</h4>
                    <p>
                        Security is built into every layer of Ignis. Your funds never leave your Bybit wallet, 
                        and multiple safety systems prevent excessive losses.
                    </p>

                    <div className="guide__poly-risks">
                        <h4 className="guide__section-title">Security Features</h4>
                        <div className="guide__poly-risk-grid">
                            <div className="guide__poly-risk-item">
                                <Icon icon="solar:check-circle-bold" className="guide__poly-risk-icon--good" />
                                <span>API keys encrypted with AES-256</span>
                            </div>
                            <div className="guide__poly-risk-item">
                                <Icon icon="solar:check-circle-bold" className="guide__poly-risk-icon--good" />
                                <span>Trade Only — No withdrawal access</span>
                            </div>
                            <div className="guide__poly-risk-item">
                                <Icon icon="solar:check-circle-bold" className="guide__poly-risk-icon--good" />
                                <span>Funds stay in YOUR Bybit wallet</span>
                            </div>
                            <div className="guide__poly-risk-item">
                                <Icon icon="solar:check-circle-bold" className="guide__poly-risk-icon--good" />
                                <span>Stop loss on every trade</span>
                            </div>
                            <div className="guide__poly-risk-item">
                                <Icon icon="solar:check-circle-bold" className="guide__poly-risk-icon--good" />
                                <span>Breakeven protection after TP1</span>
                            </div>
                            <div className="guide__poly-risk-item">
                                <Icon icon="solar:check-circle-bold" className="guide__poly-risk-icon--good" />
                                <span>Daily loss circuit breaker</span>
                            </div>
                        </div>
                    </div>

                    <div className="guide__poly-example">
                        <div className="guide__poly-header">
                            <Icon icon="solar:shield-bold" />
                            <span>Built-In Risk Protection</span>
                        </div>
                        <div className="guide__poly-content">
                            <div className="guide__poly-levels">
                                <div className="guide__poly-level">
                                    <span className="guide__poly-label">Daily Breaker</span>
                                    <span className="guide__poly-value">Auto-halt</span>
                                    <span className="guide__poly-note">Stops trading after consecutive losses</span>
                                </div>
                                <div className="guide__poly-level guide__poly-level--our">
                                    <span className="guide__poly-label">Profit Lock</span>
                                    <span className="guide__poly-value">Protects gains</span>
                                    <span className="guide__poly-note">Pauses trading after profitable streak</span>
                                </div>
                                <div className="guide__poly-level guide__poly-level--profit">
                                    <span className="guide__poly-label">Asset Cooldown</span>
                                    <span className="guide__poly-value">Prevents overtrading</span>
                                    <span className="guide__poly-note">Freezes asset after multiple stops</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="guide__key-point">
                        <Icon icon="solar:lightbulb-bolt-bold" />
                        <p>
                            <strong>Important:</strong> While Ignis has robust risk management, all trading involves risk. 
                            Never allocate capital you can't afford to lose. Ignis minimizes risk but cannot eliminate it entirely.
                        </p>
                    </div>
                </div>
            )
        },
        {
            id: 'rules',
            icon: 'solar:medal-ribbons-star-bold',
            title: 'Golden Rules for Success',
            subtitle: 'Follow these and stay profitable',
            content: (
                <div className="guide__rules">
                    <p className="guide__rules-intro">
                        Even with automated trading, your discipline matters. Ignis handles execution, 
                        but these rules will maximize your success.
                    </p>

                    <div className="guide__rules-grid">
                        <div className="guide__rule guide__rule--critical">
                            <div className="guide__rule-number">1</div>
                            <div className="guide__rule-content">
                                <h5>Never Trade with Scared Money</h5>
                                <p>If losing this money would hurt your life—rent, food, bills—don't trade it. Period. Emotional trading = losing trading.</p>
                            </div>
                        </div>

                        <div className="guide__rule guide__rule--critical">
                            <div className="guide__rule-number">2</div>
                            <div className="guide__rule-content">
                                <h5>Don't Interfere With Ignis</h5>
                                <p>Ignis manages entries, exits, and risk automatically. Opening manual trades on the same assets creates conflicts and can cause losses.</p>
                            </div>
                        </div>

                        <div className="guide__rule">
                            <div className="guide__rule-number">3</div>
                            <div className="guide__rule-content">
                                <h5>Trust Ignis's Position Sizing</h5>
                                <p>Ignis calculates position sizes based on your tier's capital cap. Don't add extra funds mid-trade or try to override Ignis's allocation.</p>
                            </div>
                        </div>

                        <div className="guide__rule">
                            <div className="guide__rule-number">4</div>
                            <div className="guide__rule-content">
                                <h5>Ignis Uses Stop Losses — Don't Remove Them</h5>
                                <p>Every trade has an automatic stop loss. Never log into Bybit and cancel or move a stop Ignis placed. That protection is there for a reason.</p>
                            </div>
                        </div>

                        <div className="guide__rule">
                            <div className="guide__rule-number">5</div>
                            <div className="guide__rule-content">
                                <h5>Let Ignis Take Profits</h5>
                                <p>Ignis closes positions at TP1, TP2, and TP3 automatically. Don't manually close trades early — you'll miss the full move Ignis is targeting.</p>
                            </div>
                        </div>

                        <div className="guide__rule">
                            <div className="guide__rule-number">6</div>
                            <div className="guide__rule-content">
                                <h5>Don't Panic on Red Days</h5>
                                <p>Some trades will hit stop loss — that's normal. Ignis's edge compounds over weeks, not individual trades. Trust the process and stay patient.</p>
                            </div>
                        </div>

                        <div className="guide__rule">
                            <div className="guide__rule-number">7</div>
                            <div className="guide__rule-content">
                                <h5>Review Your Dashboard Weekly</h5>
                                <p>Check your win rate, PnL, and trade history in the dashboard. Ignis logs everything — use it to understand performance over time.</p>
                            </div>
                        </div>
                    </div>

                    <div className="guide__final-cta">
                        <h4>Ready to Start?</h4>
                        <p>Set up Ignis and let the AI agent handle the execution. Your job is to fund, monitor, and stay disciplined.</p>
                        <div className="guide__cta-buttons">
                            <button 
                                className="guide__btn guide__btn--primary"
                                onClick={() => navigate('/crypto')}
                            >
                                <Icon icon="solar:chart-2-bold" />
                                <span>View Ignis</span>
                            </button>
                            <button 
                                className="guide__btn guide__btn--secondary"
                                onClick={() => navigate('/results')}
                            >
                                <Icon icon="solar:cup-first-linear" />
                                <span>See Our Track Record</span>
                            </button>
                        </div>
                    </div>
                </div>
            )
        }
    ];

    if (!isVisible) return null;

    const currentStepData = steps[currentStep];
    const isLastStep = currentStep === steps.length - 1;
    const isFirstStep = currentStep === 0;

    // Popup Mode
    if (isPopup) {
        return (
            <div className="guide-popup__overlay">
                <div className="guide-popup">
                    {/* Close Button */}
                    <button className="guide-popup__close" onClick={handleDismiss}>
                        <Icon icon="solar:close-circle-bold" />
                    </button>

                    {/* Progress */}
                    <div className="guide-popup__progress">
                        {steps.map((step, index) => (
                            <button
                                key={step.id}
                                className={`guide-popup__progress-dot ${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
                                onClick={() => handleGoToStep(index)}
                                title={step.title}
                            >
                                <Icon icon={step.icon} />
                            </button>
                        ))}
                    </div>

                    {/* Header */}
                    <div className="guide-popup__header">
                        <div className="guide-popup__icon" style={{ background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)' }}>
                            <Icon icon={currentStepData.icon} />
                        </div>
                        <h2 className="guide-popup__title">{currentStepData.title}</h2>
                        <p className="guide-popup__subtitle">{currentStepData.subtitle}</p>
                    </div>

                    {/* Content */}
                    <div className="guide-popup__content">
                        {currentStepData.content}
                    </div>

                    {/* Footer */}
                    <div className="guide-popup__footer">
                        {!isFirstStep && (
                            <button className="guide-popup__btn guide-popup__btn--ghost" onClick={handlePrev}>
                                <Icon icon="solar:arrow-left-linear" />
                                <span>Back</span>
                            </button>
                        )}
                        
                        {isFirstStep && <div />}

                        {isLastStep ? (
                            <button className="guide-popup__btn guide-popup__btn--primary" onClick={handleDismiss}>
                                <Icon icon="solar:check-circle-bold" />
                                <span>Got It!</span>
                            </button>
                        ) : (
                            <button className="guide-popup__btn guide-popup__btn--primary" onClick={handleNext}>
                                <span>Next</span>
                                <Icon icon="solar:arrow-right-linear" />
                            </button>
                        )}
                    </div>

                    {/* Skip Link */}
                    <button className="guide-popup__skip" onClick={handleDismiss}>
                        Skip for now
                    </button>
                </div>
            </div>
        );
    }

    // Standalone Page Mode
    return (
        <section className="guide-page">
            <div className="guide-page__inner">
                {/* Page Header */}
                <div className="guide-page__header">
                    <div className="guide-page__eyebrow">
                        <Icon icon="solar:cup-star-bold" />
                        <span>Profit Guide</span>
                    </div>
                    <h1 className="guide-page__title">
                        How to Make <span className="guide-page__highlight">Consistent Profits</span>
                    </h1>
                    <p className="guide-page__subtitle">
                        Your complete guide to winning with AiPredict's Ignis
                    </p>
                </div>

                {/* Step Navigation */}
                <div className="guide-page__nav">
                    {steps.map((step, index) => (
                        <button
                            key={step.id}
                            className={`guide-page__nav-item ${index === currentStep ? 'active' : ''}`}
                            onClick={() => handleGoToStep(index)}
                        >
                            <Icon icon={step.icon} className="guide-page__nav-icon" />
                            <span>{step.title}</span>
                        </button>
                    ))}
                </div>

                {/* Content Card */}
                <div className="guide-page__card">
                    <div className="guide-page__card-header">
                        <div className="guide-page__card-icon">
                            <Icon icon={currentStepData.icon} />
                        </div>
                        <div>
                            <h2 className="guide-page__card-title">{currentStepData.title}</h2>
                            <p className="guide-page__card-subtitle">{currentStepData.subtitle}</p>
                        </div>
                    </div>

                    <div className="guide-page__card-content">
                        {currentStepData.content}
                    </div>

                    {/* Navigation Buttons */}
                    <div className="guide-page__card-footer">
                        <button 
                            className="guide-page__btn guide-page__btn--ghost"
                            onClick={handlePrev}
                            disabled={isFirstStep}
                        >
                            <Icon icon="solar:arrow-left-linear" />
                            <span>Previous</span>
                        </button>

                        <span className="guide-page__step-indicator">
                            {currentStep + 1} of {steps.length}
                        </span>

                        <button 
                            className="guide-page__btn guide-page__btn--primary"
                            onClick={isLastStep ? () => navigate('/crypto') : handleNext}
                        >
                            <span>{isLastStep ? 'Start Trading' : 'Next'}</span>
                            <Icon icon={isLastStep ? "solar:arrow-right-up-linear" : "solar:arrow-right-linear"} />
                        </button>
                    </div>
                </div>

                {/* Quick Links */}
                <div className="guide-page__quick-links">
                    <button onClick={() => navigate('/faq')} className="guide-page__quick-link">
                        <Icon icon="solar:question-circle-linear" />
                        <span>FAQ</span>
                    </button>
                    <button onClick={() => navigate('/results')} className="guide-page__quick-link">
                        <Icon icon="solar:chart-linear" />
                        <span>View Results</span>
                    </button>
                    <button onClick={() => navigate('/support')} className="guide-page__quick-link">
                        <Icon icon="solar:chat-round-dots-linear" />
                        <span>Get Support</span>
                    </button>
                </div>
            </div>
        </section>
    );
};

export default ProfitGuide;